/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import lottie from 'lottie-web';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { TableUtility } from '@utility/dynamic-table';
import { useAuth } from '../../../Hooks/useAuth';
import { serviceName } from '../../../Api/resources/serviceConstants';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import {
  PEOPLE_VS_PROJECT_COUNT,
  PEOPLE_VS_PROJECT_COUNT_EXPORT,
  PEOPLE_VS_PROJECT_COUNT_FILTERS,
} from '../../../Api/resources/endPoint';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import './PeopleVsProjectCount.scss';

const PAGE_TITLE = 'People Vs Project Count';

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const buildProjectServiceApiUrl = (path) => {
  const servicePath = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${servicePath}/api${path}`;
  }
  return `${baseUrl}:${servicePort}${servicePath}/api${path}`;
};

const reportApiUrl = buildProjectServiceApiUrl(PEOPLE_VS_PROJECT_COUNT);
const exportApiUrl = buildProjectServiceApiUrl(PEOPLE_VS_PROJECT_COUNT_EXPORT);
const filtersApiUrl = buildProjectServiceApiUrl(PEOPLE_VS_PROJECT_COUNT_FILTERS);

const EmptyState = ({ message }) => {
  const animationContainer = useRef(null);
  useEffect(() => {
    if (!animationContainer.current) return undefined;
    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: emptyStateAnimationData,
    });
    return () => animation.destroy();
  }, []);
  return (
    <div className="people-vs-project-count-empty-state">
      <div className="people-vs-project-count-empty-state-card">
        <div ref={animationContainer} className="people-vs-project-count-empty-state-animation" />
        <p className="people-vs-project-count-empty-state-text">{message}</p>
      </div>
    </div>
  );
};

EmptyState.propTypes = { message: PropTypes.string.isRequired };

const mapFilterOptions = (opts = []) => opts.map((opt) => ({
  label: String(opt.label ?? opt.value ?? ''),
  value: String(opt.value ?? ''),
}));

const ToolbarHeading = ({ note }) => (
  <div className="people-vs-project-count-toolbar-heading">
    <span>{PAGE_TITLE}</span>
    {note ? (
      <span className="people-vs-project-count-info-icon" aria-label="People vs Project Count note">
        <i className="pi pi-info-circle" />
        <span className="people-vs-project-count-tooltip" role="tooltip">
          {note}
        </span>
      </span>
    ) : null}
  </div>
);

ToolbarHeading.propTypes = {
  note: PropTypes.string,
};

ToolbarHeading.defaultProps = {
  note: '',
};

const PeopleVsProjectCount = () => {
  const { userProfile } = useAuth();
  const authToken = userProfile?.access_token || getStoredAuthToken();

  const userInfo = useMemo(() => ({
    id: userProfile?.employeeId,
    emailId: userProfile?.email || '',
    employeeId: userProfile?.employeeId || '',
    permissions: userProfile?.permissions || {},
    roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [
    userProfile?.employeeId,
    userProfile?.email,
    userProfile?.permissions,
    userProfile?.roleDetails,
    userProfile?.siteConfiguration?.global_colors,
  ]);

  const todayStr = dayjs().format('YYYY-MM-DD');

  const [filterColumns, setFilterColumns] = useState([]);

  // Set default applied filter payload to today's date
  const [isFilterApplied, setIsFilterApplied] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState([
    { field: 'asOfDate', selectedValue: todayStr, value: todayStr },
  ]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [tableNote, setTableNote] = useState('');

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await fetch(filtersApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token: authToken },
          body: JSON.stringify({ userInfo }),
        });
        const json = await response.json();
        const { tableConfiguration = [], verticals = [], departments = [], designations = [] } = json?.data || {};

        // Guarantee fallback date column has today's date preset
        const beDateFilter = tableConfiguration.find((col) => col.field === 'asOfDate') || {
          field: 'asOfDate',
          template: 'dayCalendar',
          header: 'Date',
          menuItems: [{
            label: 'Date',
            items: [{
              type: 'filter',
              field: 'asOfDate',
              label: 'Date',
              template: 'dayCalendar',
              selectedValue: todayStr,
              selectedValues: [todayStr],
            }],
          }],
        };

        beDateFilter.isGlobalFilter = true;
        beDateFilter.visible = false;
        beDateFilter.showFilterButtonInHeader = false;

        const buildDropdown = (field, label, opts, order) => {
          const optionsData = mapFilterOptions(opts);
          return {
            field,
            header: label,
            filter: true,
            isGlobalFilter: true,
            visible: false,
            sortable: false,
            pinnable: false,
            order,
            showFilterButtonInHeader: false,
            menuItems: [{
              label,
              items: [{
                type: 'filter',
                field,
                label,
                template: 'multiSelect',
                options: optionsData,
                values: optionsData,
                selectedValues: [],
                selectedValue: null,
              }],
            }],
          };
        };

        setFilterColumns([
          buildDropdown('employeeVertical', 'Employee Vertical', verticals, 1),
          buildDropdown('employeeDepartment', 'Employee Department', departments, 2),
          buildDropdown('employeeDesignation', 'Employee Designation', designations, 3),
          beDateFilter,
        ]);
      } catch (error) {
        toast.error('Failed to load filter options');
      } finally {
        setFiltersLoaded(true);
      }
    };
    if (authToken && userInfo.emailId) {
      fetchFilters();
    }
  }, [authToken, userInfo]);

  const configuration = useMemo(() => ({
    generalSettings: {
      isModifiedFilter: true,
      enableApiFiltering: true,
      fetchOnMount: true,
      preventInitialFetch: false, // Override block
      loadOnMount: true,
      initialApiCall: true,
      baseURL: reportApiUrl,
      tableUtilityConfig: { isModifiedFilter: true },
      headers: { token: authToken },
      userInfo,
      additionalPayloadData: {
        filter: appliedFilters,
        pagination: { limit: 10000, page: 1 },
      },
      apiEndpoints: {
        getFilteredData: { endpoint: '', enabled: true },
        getAllViews: { endpoint: '', enabled: false },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        ...(hasData ? {
          download: {
            endpoint: exportApiUrl.split('/api')[1] || 'export',
            enabled: true,
            filename: 'People_Vs_Project_Count',
            sheetName: 'People_Vs_Project_Count',
          },
        } : {}),
      },
      showToolBar: true,
      showPagination: true,
      handler: {
        onFilterUpdate: ({ action, filters }) => {
          if (action === 'apply') {
            const hasDateSelected = filters.some((index) =>
              index.field === 'asOfDate' && (index.selectedValue || index.value || (index.range && index.range[0])),
            );
            if (!hasDateSelected) {
              toast.error('Please select a date before applying.');
              return;
            }
            setAppliedFilters(filters);
            setIsFilterApplied(true);
          } else if (action === 'reset') {
            setAppliedFilters([]);
            setIsFilterApplied(false);
            setHasData(false);
          }
        },
        handleFilteredApiResponse: (response) => {
          const tableData = response?.data?.tableData || [];
          tableData.forEach((row) => {
            delete row.children;
          });
          setTimeout(() => {
            setHasData(tableData.length > 0);
            setTableNote(response?.data?.note || '');
          }, 0);

          if (response?.data?.tableConfiguration && filterColumns.length > 0) {
            const finalConfig = response.data.tableConfiguration.map((beCol) => {
              const col = { ...beCol };
              col.align = 'left';
              if (col.field === 'employeeVertical') {
                col.className = 'pvp-vertical-column';
                col.headerClassName = 'pvp-vertical-column';
              }
              return col;
            });
            filterColumns.forEach((pCol) => {
              const existingIdx = finalConfig.findIndex(index => index.field === pCol.field);
              const activeFilter = appliedFilters.find((val) => val.field === pCol.field);
              let mergedMenuItems = pCol.menuItems;
              if (activeFilter) {
                mergedMenuItems = pCol.menuItems?.map((menu) => ({
                  ...menu,
                  items: menu.items?.map((item) => ({ ...item, ...activeFilter })),
                }));
              }
              if (existingIdx >= 0) {
                finalConfig[existingIdx] = {
                  ...finalConfig[existingIdx],
                  isGlobalFilter: true,
                  menuItems: mergedMenuItems,
                  filter: true,
                  showFilterButtonInHeader: false,
                };
              } else {
                finalConfig.push({ ...pCol, menuItems: mergedMenuItems });
              }
            });
            response.data.tableConfiguration = finalConfig;
          }

          const dummyPagination = { page: 1, limit: 10000, total: tableData.length, totalPages: 1 };
          if (response) {
            response.meta = response.meta || dummyPagination;
            response.pagination = response.pagination || dummyPagination;
            if (response.data) {
              response.data.showExpandCollapse = false;
              response.data.enableTreeTable = false;
              response.data.meta = response.data.meta || dummyPagination;
              response.data.pagination = response.data.pagination || dummyPagination;
            }
          }
          return response;
        },
      },
      toolBarSettings: {
        showToolBarHeading: true,
        toolBarHeading: <ToolbarHeading note={tableNote} />,
        showColumnFilterMenu: true,
        showDownloadExcel: hasData ? true : false,
        showDownloadCsv: false,
        showAddNew: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
      },
    },
    tableSettings: {
      enableTreeTable: false,
      size: 'small',
      loaderSkeleton: { rows: 5, bodyCells: 1, columns: 8 },
      pageSize: 10000,
      showPageSizeSelector: false,
      paginationConfig: { page: 1, limit: 10000, rowsPerPageOptions: [10000] },
      showExpandCollapse: false,
      tableConfig: filterColumns,
      maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      tableClassName: 'custom-table-people-vs-project-count',
    },
  }), [filterColumns, appliedFilters, authToken, userInfo, hasData]);

  return (
    <div className="people-vs-project-count-page filter-class">
      {filtersLoaded && (
        <>
          <TableUtility
            configuration={configuration}
            emptyStateComponent={
              !isFilterApplied ? (
                <EmptyState message="Select the filters above to see the results" />
              ) : undefined
            }
          />
        </>
      )}
    </div>
  );
};

export default PeopleVsProjectCount;
